import moment from 'moment-timezone';
import { IFilterStats } from './interfaces';
import { STANDARD_DATE } from './dateHelper';
import { ChartData, ChartDataset, ChartOptions } from 'chart.js';

export const getWeekRange = (date: Date | string, isMondayStart = false) => {
  const mDate = moment(date);
  const weekType = isMondayStart ? 'isoWeek' : 'week';

  return {
    start: mDate.clone().startOf(weekType).toDate(),
    end: mDate.clone().endOf(weekType).toDate(),
  };
};

export const getDatesWeek = ({ fromDate, toDate }: IFilterStats): string[] => {
  const labels: string[] = [];
  const current = moment(fromDate);
  const end = moment(toDate);

  while (current.isSameOrBefore(end, 'day')) {
    labels.push(current.format(STANDARD_DATE));
    current.add(1, 'day');
  }

  return labels;
};

const roundUpToNearestTick = (value: number): number => {
  const defaultTick = 10;
  if (value <= 0) return defaultTick;

  const magnitude = Math.pow(defaultTick, Math.floor(Math.log10(value)));
  const fraction = value / magnitude;
  let niceMultiplier = 10;
  if (fraction <= 1) niceMultiplier = 1;
  else if (fraction <= 1.5) niceMultiplier = 2; // e.g., 120 -> 200
  else if (fraction <= 2) niceMultiplier = 2.5; // e.g., 180 -> 250
  else if (fraction <= 5) niceMultiplier = 5; // e.g., 345 -> 500
  // If > 5 (e.g., 750), niceMultiplier remains 10 (rounds to 1000)

  return Math.max(niceMultiplier * magnitude, defaultTick);
};

export const calculateMaxYTick = (datasets: ChartDataset<'bar'>[]): number => {
  if (!datasets || datasets.length === 0) return 0;

  let maxValue = 0;

  const dataLength = datasets[0].data.length;

  for (let i = 0; i < dataLength; i++) {
    let dailySum = 0;
    for (const dataset of datasets) {
      const val = dataset.data[i];
      if (typeof val === 'number') {
        dailySum += val;
      }
    }
    if (dailySum > maxValue) {
      maxValue = dailySum;
    }
  }

  return roundUpToNearestTick(maxValue);
};

export const calculateTotal = (dataset: ChartDataset): number => {
  return Math.round(
    dataset.data.reduce((sum: number, value) => {
      if (typeof value === 'number') {
        return sum + value;
      }
      return sum;
    }, 0),
  );
};

export const getBarOptions = (data: ChartData<'bar'>): ChartOptions<'bar'> => {
  return {
    scales: {
      x: {
        stacked: true,
        border: {
          dash: [5, 5],
          color: 'rgba(211, 211, 211, 0.5)',
        },
        ticks: {
          callback: function (value: string | number, index: number) {
            const label = data.labels?.[index];

            if (!label) return '';
            const date = moment(label as string, STANDARD_DATE);
            if (date.isSame(moment(), 'day')) {
              return 'Today';
            }
            return date.format('ddd');
          },
          font: {
            size: 11,
          },
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        max: calculateMaxYTick(data.datasets as ChartDataset<'bar'>[]),
        ticks: {
          font: {
            size: 11,
          },
        },
        border: {
          dash: [5, 5],
          color: 'rgba(211, 211, 211, 0.5)',
        },
      },
    },
  };
};
